import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { differenceInSeconds } from 'date-fns';
import { alertActions, user_profileActions } from '../../../../../actions';
import ContentList from '../../../ContentPage/list';
import { BasicSpinner } from '../../../../../components';
import { StatisticsContainer } from './StatisticsContainer';
import { formatRangeLabel } from './formatters';
import { statisticsErrorMessage } from './statisticsError';
import useContentListPopupHook from '../../../../../hooks/useContentListPopupHook';

const PER_PAGE = 10;

// Compact relative time to match the list design: 3d ago / 2h ago / 1mo ago.
const ACTIVITY_UNITS = [
  [31536000, 'y'],
  [2592000, 'mo'],
  [604800, 'w'],
  [86400, 'd'],
  [3600, 'h'],
  [60, 'm'],
  [1, 's'],
];

function formatLastActivity(isoTimestamp) {
  if (!isoTimestamp) return '';

  const seconds = Math.max(0, differenceInSeconds(new Date(), new Date(isoTimestamp)));
  const unit = ACTIVITY_UNITS.find(([unitSeconds]) => seconds >= unitSeconds);

  if (!unit) return 'just now';

  const [unitSeconds, label] = unit;
  return `${Math.floor(seconds / unitSeconds)}${label} ago`;
}

// Maps a statistics_cards API row onto the shape WorkspaceCardRow renders.
function normalizeCard(card) {
  return {
    content_group_id: card.id,
    name: card.name,
    workspace_name: card.workspace_name,
    workspace_id: card.workspace_id,
    time_spent_ms: card.time_spent_ms,
    // Backend-rendered ("31m"); ms is kept only to scale the bar.
    time_spent_display: card.time_spent_display,
    last_activity_ago: formatLastActivity(card.last_activity_at),
  };
}

// Bar width is relative to the longest card across all loaded pages. Cards arrive
// sorted by time_spent_ms desc, so the first card anchors the scale.
function withBarWidths(cards) {
  const maxMs = Math.max(...cards.map((card) => card.time_spent_ms), 1);

  return cards.map((card) => ({
    ...card,
    bar_width: (card.time_spent_ms / maxMs) * 100,
  }));
}

function WorkspaceCardRow({
  card,
  index,
  handleCardClick
}){

  return(
     <div
      className="time-spent-card"
      onClick={() => handleCardClick(card)}
    >
      <div className="time-spent-card__rank">{index + 1}</div>

      <div className="time-spent-card__body">
        <div className="time-spent-card__title">
          <span className="time-spent-card__name text-truncate">{card.name}</span>
          <span className="time-spent-card__workspace">{card.workspace_name}</span>
        </div>

        <div className="time-spent-card__bar-row">
          <div className="time-spent-card__bar">
            <div
              className="time-spent-card__bar-fill"
              style={{ width: `${card.bar_width}%` }}
            />

            <span className="time-spent-card__time">{card.time_spent_display}</span>
          </div>
        </div>
      </div>

      <div className="time-spent-card__ago">{card.last_activity_ago}</div>
    </div>
  )
}

// Fetches the paginated "time spent in cards" list. Resets to page 1 whenever the
// user, date range, or filters change; loadNextPage appends the following page.
function useProductivityCards({ userId, range, excludedWorkspaceIds, excludedOrganizationIds }) {
  const dispatch = useDispatch();
  const [page, setPage] = useState(1);
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(true);

  const excludedWorkspaceKey = excludedWorkspaceIds.join(',');
  const excludedOrganizationKey = excludedOrganizationIds.join(',');

  const loadPage = useCallback((pageToLoad, { append }) => {
    setLoading(true);

    return user_profileActions
      .fetchProductivityCards(userId, range, excludedWorkspaceIds, excludedOrganizationIds, pageToLoad, PER_PAGE)
      .then((data) => {
        if (!data || data.status !== 'ok') return;

        const incoming = data.cards.map(normalizeCard);
        setCards((prevCards) => (append ? [...prevCards, ...incoming] : incoming));
        setHasNextPage(data.has_next_page);
      })
      // A rejected range (400) or a throttle (429) would otherwise read as an empty
      // list. Retiring "Load more" also stops a failed append from being retried
      // straight into the same throttle.
      .catch((error) => {
        if (!append) setCards([]);
        setHasNextPage(false);
        dispatch(alertActions.error(statisticsErrorMessage(error)));
      })
      .finally(() => setLoading(false));
    // excludedWorkspaceIds / excludedOrganizationIds are tracked via their joined keys.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, range, excludedWorkspaceKey, excludedOrganizationKey]);

  useEffect(() => {
    setPage(1);
    loadPage(1, { append: false });
  }, [loadPage]);

  const loadNextPage = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    loadPage(nextPage, { append: true });
  };

  return { cards, loading, hasNextPage, loadNextPage };
}

function TimeSpentInCards({ userId, range, excludedWorkspaceIds = [], excludedOrganizationIds = [] }) {
  const { cards, loading, hasNextPage, loadNextPage } = useProductivityCards({
    userId,
    range,
    excludedWorkspaceIds,
    excludedOrganizationIds,
  });
  const { contentGroupPopUp, openContentGroupPopUp, contentListProps } = useContentListPopupHook();

  const handleCardClick = (card) => openContentGroupPopUp({
    content_group_id: card.content_group_id,
    workspace_id: card.workspace_id,
  });

  const rangeLabel = formatRangeLabel(range);
  const decoratedCards = withBarWidths(cards);
  const isEmpty = !loading && cards.length === 0;

  return (
    <StatisticsContainer>
      <div className="user_profile-statistics-time-spent__header">
        <h2>Time Spent in Cards</h2>
        {rangeLabel && <span className="user_profile-statistics-time-spent__range">{rangeLabel}</span>}
      </div>

      <div className="user_profile-statistics-time-spent">
        <div className="time-spent-card__column-label">Card</div>
        <div 
          className='time-spent-card__table bg-white rounded'
        >
          {decoratedCards.map((card, index) => (
            <WorkspaceCardRow
              card={card}
              key={card.content_group_id}
              index={index}
              handleCardClick={handleCardClick}
            />
          ))}

          {loading && <BasicSpinner />}

          {isEmpty && (
            <div className="d-flex justify-content-center py-4">
              There are no time-tracked cards.
            </div>
          )}

          {!loading && hasNextPage && (
            <div className='time-spent-card__footer'>
              <button type="button" className="time-spent-card__load-more" onClick={loadNextPage}>
                Load {PER_PAGE} more...
              </button>
            </div>
          )}

        </div>
      </div>

      {contentGroupPopUp.show && (
        <ContentList {...contentListProps} />
      )}
    </StatisticsContainer>
  );
}

export default TimeSpentInCards;
